import { resolveAppTheme } from "@token/shared";
import { resolveBranding } from "../../branding";
import {
  PaymentProviderName,
  type AppConfig,
  type AuthProviderName,
  type ConfigSource,
  type PaymentEnvironment,
} from "../types";

export class EnvConfigSource implements ConfigSource {
  readonly name = "env";

  async load(): Promise<AppConfig> {
    const providers = parseProviders(import.meta.env.VITE_PORTAL_AUTH_PROVIDERS) ?? ["mock"];
    const paymentEnvironment = parsePaymentEnvironment(import.meta.env.VITE_PORTAL_ADYEN_ENVIRONMENT);
    return {
      auth: {
        providers,
        auth0:
          providers.includes("auth0")
            ? {
                domain: requireEnv("VITE_PORTAL_AUTH0_DOMAIN"),
                clientId: requireEnv("VITE_PORTAL_AUTH0_CLIENT_ID"),
                audience: import.meta.env.VITE_PORTAL_AUTH0_AUDIENCE,
                scope: import.meta.env.VITE_PORTAL_AUTH0_SCOPE,
                redirectUri: import.meta.env.VITE_PORTAL_AUTH0_REDIRECT_URI,
              }
            : undefined,
        google:
          providers.includes("google")
            ? {
                clientId: requireEnv("VITE_PORTAL_GOOGLE_CLIENT_ID"),
                hostedDomain: import.meta.env.VITE_PORTAL_GOOGLE_HOSTED_DOMAIN,
              }
            : undefined,
        ...(providers.includes("demo")
          ? { demo: { loginUri: import.meta.env.VITE_PORTAL_DEMO_AUTH_LOGIN_URI || "/api/portal/auth/demo/login" } }
          : {}),
      },
      payment: {
        provider:
          parsePaymentProvider(import.meta.env.VITE_PORTAL_PAYMENT_PROVIDER) ??
          PaymentProviderName.Mock,
        ...(paymentEnvironment ? { environment: paymentEnvironment } : {}),
      },
      apiUrl: import.meta.env.VITE_API_URL,
      theme: resolveAppTheme(import.meta.env.VITE_PORTAL_THEME),
      branding: resolveBranding(import.meta.env.VITE_PORTAL_BRANDING),
    };
  }
}

export function parsePaymentProvider(
  value: string | undefined,
): PaymentProviderName | undefined {
  const normalizedValue = value?.trim().toUpperCase();
  if (normalizedValue === PaymentProviderName.Mock) return PaymentProviderName.Mock;
  if (normalizedValue === PaymentProviderName.Adyen) return PaymentProviderName.Adyen;
  if (normalizedValue === PaymentProviderName.Stripe) return PaymentProviderName.Stripe;
  return undefined;
}

export function parsePaymentEnvironment(
  value: string | undefined,
): PaymentEnvironment | undefined {
  const normalizedValue = value?.trim().toLowerCase();
  if (normalizedValue === "test" || normalizedValue === "live") {
    return normalizedValue;
  }
  return undefined;
}

function requireEnv(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Required env var ${String(key)} is not set`);
  }
  return value;
}

function parseProviders(value: string | undefined): AuthProviderName[] | undefined {
  if (!value) return undefined;
  return value
    .split(",")
    .map((provider) => provider.trim())
    .filter(Boolean) as AuthProviderName[];
}
